import { createSlice } from '@reduxjs/toolkit';
import { apiCallBegan } from './middleware/apiMiddleware';

const initialState = {
    categories: [],
    fetchStatus: 'idle',
    fetchError: null,

    createStatus: 'idle',
    createError: null,

    updateStatus: 'idle',
    updateError: null,

    deleteStatus: 'idle',
    deleteError: null,
};

const categorySlice = createSlice({
    name: 'category',
    initialState,
    reducers: {
        // Fetch
        fetchCategoriesRequested: (state) => {
            state.fetchStatus = 'loading';
            state.fetchError = null;
        },
        fetchCategoriesReceived: (state, action) => {
            state.fetchStatus = 'succeeded';
            const raw = action.payload?.data || action.payload;
            state.categories = Array.isArray(raw) ? raw : [];
        },
        fetchCategoriesFailed: (state, action) => {
            state.fetchStatus = 'failed';
            state.fetchError = action.payload?.errorMessage || 'Failed to fetch categories';
        },

        // Create
        createCategoryRequested: (state) => {
            state.createStatus = 'loading';
            state.createError = null;
        },
        createCategoryReceived: (state, action) => {
            state.createStatus = 'succeeded';
            const created = action.payload;
            if (Array.isArray(created)) {
                state.categories.push(...created);
            } else if (created?.id) {
                state.categories.push(created);
            }
        },
        createCategoryFailed: (state, action) => {
            state.createStatus = 'failed';
            state.createError = action.payload?.errorMessage || 'Failed to create category';
        },
        resetCreateStatus: (state) => {
            state.createStatus = 'idle';
            state.createError = null;
        },

        // Update
        updateCategoryRequested: (state) => {
            state.updateStatus = 'loading';
            state.updateError = null;
        },
        updateCategoryReceived: (state, action) => {
            state.updateStatus = 'succeeded';
            const updated = action.payload?.data || action.payload;
            if (updated?.id) {
                const idx = state.categories.findIndex((c) => c.id === updated.id);
                if (idx !== -1) state.categories[idx] = updated;
            }
        },
        updateCategoryFailed: (state, action) => {
            state.updateStatus = 'failed';
            state.updateError = action.payload?.errorMessage || 'Failed to update category';
        },
        resetUpdateStatus: (state) => {
            state.updateStatus = 'idle';
            state.updateError = null;
        },

        // Delete
        deleteCategoryRequested: (state) => {
            state.deleteStatus = 'loading';
            state.deleteError = null;
        },
        deleteCategoryReceived: (state) => {
            state.deleteStatus = 'succeeded';
        },
        deleteCategoryFailed: (state, action) => {
            state.deleteStatus = 'failed';
            state.deleteError = action.payload?.errorMessage || 'Failed to delete category';
        },
        resetDeleteStatus: (state) => {
            state.deleteStatus = 'idle';
            state.deleteError = null;
        },
    },
});

export const {
    fetchCategoriesRequested,
    fetchCategoriesReceived,
    fetchCategoriesFailed,
    createCategoryRequested,
    createCategoryReceived,
    createCategoryFailed,
    resetCreateStatus,
    updateCategoryRequested,
    updateCategoryReceived,
    updateCategoryFailed,
    resetUpdateStatus,
    deleteCategoryRequested,
    deleteCategoryReceived,
    deleteCategoryFailed,
    resetDeleteStatus,
} = categorySlice.actions;

// Thunks
export const fetchCategories = () => (dispatch) => {
    dispatch(
        apiCallBegan({
            url: '/event-categories',
            method: 'GET',
            onStart: categorySlice.actions.fetchCategoriesRequested.type,
            onSuccess: categorySlice.actions.fetchCategoriesReceived.type,
            onError: categorySlice.actions.fetchCategoriesFailed.type,
        })
    );
};

export const createCategory = ({ name, slug }) => (dispatch) => {
    dispatch(
        apiCallBegan({
            url: '/event-categories',
            method: 'POST',
            data: [{ name, slug }],
            onStart: categorySlice.actions.createCategoryRequested.type,
            onSuccess: categorySlice.actions.createCategoryReceived.type,
            onError: categorySlice.actions.createCategoryFailed.type,
        })
    );
};

export const updateCategory = (id, { name, slug }) => (dispatch) => {
    dispatch(
        apiCallBegan({
            url: `/event-categories/${id}`,
            method: 'PUT',
            data: { name, slug },
            onStart: categorySlice.actions.updateCategoryRequested.type,
            onSuccess: categorySlice.actions.updateCategoryReceived.type,
            onError: categorySlice.actions.updateCategoryFailed.type,
        })
    );
};

export const deleteCategory = (id) => (dispatch) => {
    dispatch(
        apiCallBegan({
            url: `/event-categories/${id}`,
            method: 'DELETE',
            onStart: categorySlice.actions.deleteCategoryRequested.type,
            onSuccess: categorySlice.actions.deleteCategoryReceived.type,
            onError: categorySlice.actions.deleteCategoryFailed.type,
        })
    );
};

export default categorySlice.reducer;
